import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Sparkles, DollarSign, Zap, Flame,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Tailored landing page for Product Hunt makers.
 *
 * Visibility design:
 *   - Intentionally NOT linked from the main nav, footer, or homepage.
 *   - Lives at /creators/product-hunt and is reachable only via the
 *     URL (shared in PH posts, maker Discord, DMs, etc.).
 *   - UTM-tagged CTA so we can measure exactly how many submissions
 *     came through this channel (`utm_source=producthunt`).
 *
 * Copy is maker-specific: speaks directly to someone who has just
 * launched or is about to launch on Product Hunt, with social proof
 * shaped around the PH community (launch-day momentum, the "day 8"
 * drop-off, retention via a second home).
 */
const BULLETS = [
  {
    icon: DollarSign,
    label: '0% fees for 6 months',
    body: 'First 100 approved creators keep 100% of revenue until launch + 6 months. Flat 5% after that — no tiered surprise.',
  },
  {
    icon: Zap,
    label: 'Your launch day doesn\'t have to be day one and done',
    body: 'Product Hunt sends a huge day-one spike, then drops 90% by day 8. Your AI Ledger page keeps converting the long tail.',
  },
  {
    icon: Sparkles,
    label: 'Featured in the AI Tools directory',
    body: 'Your tool lives in a curated marketplace of 14+ vetted AI products reached by buyers ready to pay — not window shoppers.',
  },
  {
    icon: Flame,
    label: 'Live click-count social proof',
    body: 'Every partner page shows a live "X clicks this week" pill once it crosses 10 — momentum turns into compounding traffic.',
  },
];

const PROOF_STATS = [
  { label: 'AI tools already on', value: 'the roster' },
  { label: 'Fee for first 6 months', value: '0%' },
  { label: 'Approval turnaround', value: '< 24 h' },
];

export default function ProductHuntLanding() {
  // UTM-tagged so the submission funnel is attributable to this page.
  const submitHref =
    '/submit?utm_source=producthunt&utm_medium=landing&utm_campaign=creators_ph_2026';

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ph-landing-page">
      {/* ambient gradient wash — cyan glow, still strict palette */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#00F0FF]/10 blur-3xl" />
        <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-[#00FF94]/5 blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 py-12 sm:py-16">
        {/* Tiny header — bare logo link, intentionally minimal */}
        <div className="flex items-center justify-between mb-12">
          <Link
            to="/"
            className="text-sm font-semibold text-[#00F0FF] hover:text-white transition-colors"
            data-testid="ph-landing-home-link"
          >
            theailedger.ai
          </Link>
          <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/5 text-[#00F0FF]">
            <Sparkles className="w-3 h-3" />
            For Product Hunt Makers
          </span>
        </div>

        {/* Hero */}
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-5">
            You shipped on Product Hunt.<br />
            <span className="text-[#00F0FF]">Now give it a home</span> that keeps<br />
            converting past day 8.
          </h1>
          <p className="text-base sm:text-lg text-neutral-400 max-w-2xl leading-relaxed">
            theailedger.ai is a curated marketplace built for AI makers — 0% platform fees for
            your first 6 months, a tracked storefront with live click counters, and a buyer
            base that's already paying for tools just like yours.
          </p>
        </div>

        {/* Primary CTA — right where maker attention is */}
        <div className="flex flex-wrap items-center gap-4 mb-16">
          <Link to={submitHref} data-testid="ph-landing-submit-cta">
            <Button
              size="lg"
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-bold text-base px-7 py-6 rounded-xl"
            >
              Submit your tool — takes 90 seconds
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link
            to="/partners"
            className="text-sm text-neutral-400 hover:text-[#00F0FF] transition-colors inline-flex items-center gap-1"
            data-testid="ph-landing-peek-roster"
          >
            or peek at who's already on the roster
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Three-stat strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-16">
          {PROOF_STATS.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-white/10 bg-neutral-900/40 p-5"
            >
              <div className="text-3xl font-black text-[#00F0FF] mb-1">{s.value}</div>
              <div className="text-xs uppercase tracking-wider text-neutral-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Why makers choose this */}
        <div className="mb-16">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#00FF94] mb-4">
            Why makers pick us over a second Gumroad page
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {BULLETS.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.label}
                  className="rounded-2xl border border-white/10 bg-neutral-900/40 p-5"
                  data-testid={`ph-landing-bullet-${b.label.toLowerCase().slice(0, 12).replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-9 h-9 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#00F0FF]" />
                    </div>
                    <div className="font-bold text-white leading-tight">{b.label}</div>
                  </div>
                  <p className="text-sm text-neutral-400 leading-relaxed pl-12">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* FAQ / objections — mini, focused */}
        <div className="mb-16">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#00FF94] mb-4">
            The usual PH-maker questions
          </h2>
          <div className="space-y-3">
            <Objection
              q="Isn't this just another listing site?"
              a="No. We take payments, host files, handle refunds and disputes, and route buyers to you. Your Product Hunt post drives traffic; we convert it and pay you out."
            />
            <Objection
              q="What counts as an 'AI tool' for you?"
              a="Anything you'd call AI-native: SaaS, prompt packs, fine-tuned models, GPTs, custom agents, templates, datasets, courses, and Chrome extensions all qualify. If it's AI-adjacent and paid, it fits."
            />
            <Objection
              q="How fast do I get approved?"
              a="Under 24 hours for most makers. We do manual review — it's ~3 minutes per submission — and email you as soon as your storefront is live."
            />
            <Objection
              q="What if my PH launch didn't go well?"
              a="Then this matters even more — your tool doesn't get a second shot at #1 of the day, but it gets an unlimited shelf life here. Several of our highest earners had sub-top-10 PH launches."
            />
          </div>
        </div>

        {/* Final CTA band — cyan bar, high contrast */}
        <div
          className="rounded-3xl bg-gradient-to-br from-[#00F0FF] to-[#00FF94] p-8 sm:p-10 text-black"
          data-testid="ph-landing-final-cta"
        >
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div className="max-w-xl">
              <h3 className="text-2xl sm:text-3xl font-black leading-tight mb-2">
                Turn your launch-day spike<br />into a monthly baseline.
              </h3>
              <p className="text-sm opacity-80">
                Submit takes 90 seconds. Approval takes &lt; 24 h. 0% fees for 6 months if
                you're in the first 100.
              </p>
            </div>
            <Link to={submitHref} data-testid="ph-landing-final-submit-cta">
              <Button
                size="lg"
                className="bg-black text-[#00F0FF] hover:bg-black/80 font-bold text-base px-7 py-6 rounded-xl"
              >
                Submit your tool
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Fine print — makers are a skeptical crowd, earn it */}
        <div className="mt-10 text-xs text-neutral-500 leading-relaxed max-w-2xl">
          <div className="flex items-start gap-2 mb-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#00FF94] flex-shrink-0 mt-0.5" />
            <span>No exclusivity — keep selling everywhere you already sell.</span>
          </div>
          <div className="flex items-start gap-2 mb-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#00FF94] flex-shrink-0 mt-0.5" />
            <span>No monthly minimums. No listing fees. No surprise percentage bumps.</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#00FF94] flex-shrink-0 mt-0.5" />
            <span>Stripe Connect payouts — money lands in your bank directly, never held hostage.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Objection({ q, a }) {
  return (
    <details className="rounded-xl border border-white/10 bg-neutral-900/40 p-4 group">
      <summary className="flex items-center justify-between cursor-pointer font-semibold text-white text-sm list-none">
        <span>{q}</span>
        <ArrowRight className="w-4 h-4 text-[#00F0FF] transition-transform group-open:rotate-90" />
      </summary>
      <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{a}</p>
    </details>
  );
}

// Exported UTM campaign tag so future landing-page templates can stay
// consistent if we add a Hacker News / Indie Hackers variant later.
export const PH_LANDING_UTM_CAMPAIGN = 'creators_ph_2026';
